import type * as THREE from 'three';
import { HUD_THEME, RENDER } from '../config/tuning';
import type { Projector } from '../render/Camera';
import { HudDegradation } from './HudDegradation';
import type { HudFrame } from './HudFrame';
import { HudPainter, type HudTheme } from './HudLayout';
import { drawBearingTags } from './elements/BearingTags';
import { drawDesignation } from './elements/Designation';
import { drawExposure } from './elements/Exposure';
import { drawFpm } from './elements/Fpm';
import { drawHeadingTape } from './elements/HeadingTape';
import { computeHorizon, createHorizonGeometry, drawHorizon } from './elements/Horizon';
import { drawMissileWarning } from './elements/MissileWarning';
import { drawMissionPanel } from './elements/MissionPanel';
import { drawPitchLadder } from './elements/PitchLadder';
import { drawReadouts } from './elements/Readouts';
import { drawReticle } from './elements/Reticle';
import { drawRwr } from './elements/Rwr';
import { drawStartHold } from './elements/StartHold';
import { drawStatus } from './elements/Status';

/**
 * The HUD (SPEC §3.4): a Canvas2D vector overlay on its own canvas, layered over the WebGL view
 * and redrawn every frame.
 *
 * Canvas2D rather than 3D-projected geometry, deliberately. At 1080p the whole instrument is a
 * few hundred thin line segments — well under the 1 ms budget of SPEC §9 — and it stays crisp at
 * any resolution, needs no depth handling against the terrain, and is far simpler to reason about
 * than a mesh that has to sit in front of everything without z-fighting.
 *
 * Draw order runs back to front: attitude first, then world-anchored symbology, then the fixed
 * furniture, then warnings on top. Warnings drawing last is not cosmetic — a missile caret must
 * never end up underneath a pitch rung.
 */
export class Hud {
  readonly degradation = new HudDegradation();

  private readonly painter: HudPainter;
  private readonly canvas: HTMLCanvasElement;
  private readonly horizon = createHorizonGeometry();

  private cssWidthPx = 1;
  private cssHeightPx = 1;
  private lastDrawMsValue = 0;

  /** Toggled by the accessibility setting; see HUD_THEME. */
  private highContrast = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Hud: 2D canvas context unavailable');
    }
    this.painter = new HudPainter(context);
    this.painter.setTheme(HUD_THEME.standard);
    this.resize(canvas.clientWidth || 1, canvas.clientHeight || 1);
  }

  /** Milliseconds spent in the last draw. Shown in the debug overlay against the 1 ms budget. */
  get lastDrawMs(): number {
    return this.lastDrawMsValue;
  }

  get isHighContrast(): boolean {
    return this.highContrast;
  }

  setHighContrast(enabled: boolean): void {
    this.highContrast = enabled;
    this.painter.setTheme(enabled ? HUD_THEME.highContrast : HUD_THEME.standard);
  }

  toggleHighContrast(): void {
    this.setHighContrast(!this.highContrast);
  }

  get theme(): HudTheme {
    return this.painter.currentTheme;
  }

  /** Resize the backing store to a CSS pixel size. */
  resize(cssWidthPx: number, cssHeightPx: number): void {
    this.cssWidthPx = Math.max(1, cssWidthPx);
    this.cssHeightPx = Math.max(1, cssHeightPx);

    const ratio = Math.min(window.devicePixelRatio || 1, RENDER.maxPixelRatio);
    this.canvas.width = Math.round(this.cssWidthPx * ratio);
    this.canvas.height = Math.round(this.cssHeightPx * ratio);
    this.canvas.style.width = `${this.cssWidthPx}px`;
    this.canvas.style.height = `${this.cssHeightPx}px`;

    const context = this.canvas.getContext('2d');
    if (context !== null) {
      this.painter.setContext(context);
      // Work in CSS pixels; the device ratio is absorbed once, here.
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    }
    this.painter.layout.resize(this.cssWidthPx, this.cssHeightPx);
    this.painter.setTheme(this.painter.currentTheme);
  }

  /**
   * Blank the overlay.
   *
   * Used by the impact replay (SPEC §3.3.1): the HUD is the *drone's* display, so when the camera
   * stops being the drone's camera the instrument has to go with it. Leaving symbology floating
   * over an external shot would break the one fiction the whole presentation rests on.
   */
  clear(): void {
    this.painter.begin();
  }

  /** Match the backing store to the element's CSS size. Cheap; safe per frame. */
  syncToCanvasSize(): boolean {
    const widthPx = this.canvas.clientWidth;
    const heightPx = this.canvas.clientHeight;
    if (widthPx === this.cssWidthPx && heightPx === this.cssHeightPx) return false;
    this.resize(widthPx, heightPx);
    return true;
  }

  /**
   * Draw one frame. `cameraOrientation` is the camera's world orientation — including the install
   * tilt and any look-around — because the horizon has to be solved from where the *camera* is
   * pointing, not from the airframe's attitude.
   */
  render(
    frame: HudFrame,
    projector: Projector,
    cameraOrientation: THREE.Quaternion,
    aircraftPositionM: THREE.Vector3,
  ): void {
    const startMs = performance.now();
    const painter = this.painter;
    const layout = painter.layout;

    layout.pixelsPerRadian = projector.pixelsPerRadian;
    this.degradation.update(frame.jamming, frame.simTimeS);

    painter.begin();

    computeHorizon(cameraOrientation, projector.pixelsPerRadian, this.horizon);

    drawHorizon(painter, this.horizon);
    drawPitchLadder(painter, this.horizon, projector.pixelsPerRadian);
    drawBearingTags(painter, frame, projector, aircraftPositionM, this.degradation, this.horizon);
    drawDesignation(painter, frame, projector);
    drawFpm(painter, frame, projector, this.degradation);
    drawReticle(painter);
    drawHeadingTape(painter, frame, this.degradation);
    drawReadouts(painter, frame);
    drawStatus(painter, frame);
    drawExposure(painter, frame);
    drawMissionPanel(painter, frame);
    drawStartHold(painter, frame);
    drawRwr(painter, frame);
    drawMissileWarning(painter, frame);

    this.lastDrawMsValue = performance.now() - startMs;
  }
}
